import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { Activity, Clock, AlertTriangle, TrendingUp } from 'lucide-react';
import { useWorkflowQueue, getStatusLabel, FilaEtapa } from '@/hooks/useWorkflowQueue';

interface WorkflowQueueCardProps {
  contratoId: string;
}

const STATUS_COLORS: Record<string, string> = {
  em_apontamento: 'hsl(var(--muted-foreground))',
  aguardando_qualidade: 'hsl(35, 90%, 55%)',
  aguardando_planejamento: 'hsl(200, 70%, 55%)',
  disponivel_para_medicao: 'hsl(142, 70%, 45%)',
  vinculada_a_boletim: 'hsl(142, 50%, 35%)',
  rejeitado: 'hsl(var(--destructive))',
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload as FilaEtapa & { label: string };
  return (
    <div className="bg-popover border rounded-lg p-3 shadow-md text-sm space-y-1">
      <p className="font-medium">{d.label}</p>
      <p>Quantidade: <strong>{d.quantidade}</strong></p>
      {d.tempo_medio_horas > 0 && (
        <p>Tempo médio: <strong>{d.tempo_medio_horas.toFixed(1)}h</strong></p>
      )}
      {d.aging_3d > 0 && <p className="text-orange-500">Aging &gt;3d: {d.aging_3d}</p>}
      {d.aging_7d > 0 && <p className="text-destructive">Aging &gt;7d: {d.aging_7d}</p>}
      {d.throughput_30d > 0 && <p className="text-muted-foreground">Throughput 30d: {d.throughput_30d}</p>}
    </div>
  );
};

export const WorkflowQueueCard: React.FC<WorkflowQueueCardProps> = ({ contratoId }) => {
  const { data: filaData = [], isLoading } = useWorkflowQueue(contratoId);

  // Check if we have enough data
  const totalTransicoes = filaData.reduce((s, f) => s + f.throughput_30d, 0);
  const totalExecucoes = filaData.reduce((s, f) => s + f.quantidade, 0);
  const hasInsuficientData = totalExecucoes < 3;

  const totalAging = filaData.reduce((s, f) => s + f.aging_3d, 0);
  const avgTempo = filaData.filter(f => f.tempo_medio_horas > 0);
  const tempoMedioGeral = avgTempo.length > 0
    ? avgTempo.reduce((s, f) => s + f.tempo_medio_horas, 0) / avgTempo.length
    : 0;

  const chartData = filaData.map(f => ({
    ...f,
    label: getStatusLabel(f.status),
    fill: STATUS_COLORS[f.status] || 'hsl(var(--muted-foreground))',
  }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" /> Fila por Etapa do Workflow
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Carregando...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (filaData.length === 0 || hasInsuficientData) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" /> Fila por Etapa do Workflow
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
            <div className="text-center">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>Dados insuficientes</p>
              <p className="text-xs mt-1">Mínimo de 3 execuções necessárias para exibir indicadores.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4" /> Fila por Etapa do Workflow
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mini KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                  <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Total na Fila</p>
                    <p className="text-lg font-bold">{totalExecucoes}</p>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>Execuções distribuídas nas etapas do workflow</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Tempo Médio</p>
                    <p className="text-lg font-bold">
                      {tempoMedioGeral > 0 ? `${tempoMedioGeral.toFixed(1)}h` : 'N/A'}
                    </p>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>Tempo médio de permanência por etapa</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`flex items-center gap-2 p-2 rounded-md ${totalAging > 0 ? 'bg-destructive/10' : 'bg-muted/50'}`}>
                  <AlertTriangle className={`h-3.5 w-3.5 ${totalAging > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="text-xs text-muted-foreground">Aging &gt;3d</p>
                    <p className={`text-lg font-bold ${totalAging > 0 ? 'text-destructive' : ''}`}>{totalAging}</p>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>Execuções paradas há mais de 3 dias na mesma etapa</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Bar Chart */}
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={50}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar dataKey="quantidade" radius={[4, 4, 0, 0]} maxBarSize={48}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Aging badges */}
        {filaData.some(f => f.aging_7d > 0 || f.aging_15d > 0) && (
          <div className="flex flex-wrap gap-2">
            {filaData.filter(f => f.aging_7d > 0 || f.aging_15d > 0).map(f => (
              <Badge key={f.status} variant="destructive" className="text-xs">
                {getStatusLabel(f.status)}: {f.aging_15d > 0 ? `${f.aging_15d} >15d` : `${f.aging_7d} >7d`}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
